import { Tooltip, VisuallyHidden } from "@mantine/core";
import { Info } from "lucide-react";
import type { ReactNode } from "react";

import classes from "./FieldLabel.module.css";

export interface FieldLabelProps {
  label: ReactNode;
  /** A required field: an asterisk in the danger color after the label. */
  required?: boolean;
  /** What the field is for, in a tooltip on an info icon after the label. */
  info?: string;
  /**
   * For a label that names no input (a group's, a `div`): the info text is also in the label,
   * visually hidden, so a screen reader reads it there. An input's label uses `infoLabel`.
   */
  spoken?: boolean;
}

/**
 * A form field's label as the live forms draw it (design-system.md 3.6): the text, an accented
 * asterisk when required, and an info icon with a tooltip. Pass it as an input's `label` with
 * `withAsterisk={false}`, so Mantine's own asterisk does not repeat it.
 */
export function FieldLabel({ label, required = false, info, spoken = false }: FieldLabelProps) {
  return (
    <span className={classes.label}>
      {label}
      {required ? (
        <span className={classes.asterisk} aria-hidden>
          *
        </span>
      ) : null}
      {info === undefined ? null : (
        <Tooltip label={info} multiline withArrow maw={420}>
          <span className={classes.icon} aria-hidden>
            <Info size={16} />
          </span>
        </Tooltip>
      )}
      {spoken && info !== undefined ? <VisuallyHidden>{`: ${info}`}</VisuallyHidden> : null}
    </span>
  );
}

/**
 * An input's `label` with live's info icon, and the same words in Mantine's description slot,
 * visually hidden, so every screen reader hears what the tooltip shows (the slot is the input's
 * `aria-describedby`; text inside the label would lengthen the input's name instead). A visible
 * description, if the field has one, follows the hidden words. Without one, the slot would push
 * the input down by Mantine's description offset, so that margin is cleared.
 */
export function infoLabel(props: FieldLabelProps & { info: string; description?: ReactNode }) {
  const { description, ...label } = props;
  return {
    label: <FieldLabel {...label} />,
    description: (
      <>
        <VisuallyHidden>{`${props.info} `}</VisuallyHidden>
        {description}
      </>
    ),
    ...(description === undefined ? { styles: { wrapper: { marginTop: 0 } } } : {}),
  };
}
